import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { useAuth, API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  BookOpen, Clock, Users, GraduationCap, Shield, Monitor,
  Accessibility, UserCheck, Wrench, CalendarDays, ArrowRight, ChevronDown, ChevronUp,
  Code2, Megaphone, FileText, PlayCircle
} from "lucide-react";

const PROGRAM_ICONS = {
  "Quality Assurance": BookOpen,
  "Cybersecurity": Shield,
  "IT Foundations": Monitor,
  "Accessibility": Accessibility,
  "HR Specialist": UserCheck,
  "Certificate of Manufacturing Associate": Wrench,
  "Vibe Coding": Code2,
  "Marketing & Branding": Megaphone,
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const ProgramCard = ({ program, login, user }) => {
  const [expanded, setExpanded] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [materials, setMaterials] = useState(null);
  const [matLoading, setMatLoading] = useState(false);
  const Icon = PROGRAM_ICONS[program.name] || BookOpen;
  const isComingSoon = !program.cohorts || program.cohorts.length === 0;

  const toggleMaterials = async () => {
    const next = !materialsOpen;
    setMaterialsOpen(next);
    if (next && materials === null && !matLoading) {
      setMatLoading(true);
      try {
        const r = await fetch(`${API}/programs/${program.program_id}/materials`);
        if (r.ok) setMaterials(await r.json());
        else setMaterials({ program: [], general: [] });
      } catch (e) {
        setMaterials({ program: [], general: [] });
      } finally {
        setMatLoading(false);
      }
    }
  };
  const totalMaterials =
    (materials?.program?.length || 0) + (materials?.general?.length || 0);

  return (
    <Card className={`bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden transition-shadow hover:shadow-md ${isComingSoon ? "opacity-70" : ""}`} data-testid={`program-card-${program.program_id}`}>
      <CardContent className="p-0">
        {/* Image + Content Row */}
        <div className="flex flex-col sm:flex-row">
          {/* Program Image */}
          {program.photo_url && (
            <div className="sm:w-56 md:w-64 flex-shrink-0">
              <img
                src={program.photo_url}
                alt={program.name}
                className="w-full h-48 sm:h-full object-cover"
              />
            </div>
          )}

          {/* Content */}
          <div className="flex-1 p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 bg-brand-navy/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon className="w-6 h-6 text-brand-navy" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-heading font-bold text-slate-900 mb-1">{program.name}</h3>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  {program.duration_weeks && (
                    <span className="flex items-center gap-1.5 text-sm text-slate-500">
                      <Clock className="w-3.5 h-3.5" /> {program.duration_weeks} weeks
                    </span>
                  )}
                  {program.cohorts?.length > 0 && (
                    <span className="flex items-center gap-1.5 text-sm text-slate-500">
                      <CalendarDays className="w-3.5 h-3.5" /> {program.cohorts.length} upcoming date{program.cohorts.length !== 1 ? "s" : ""}
                    </span>
                  )}
                  {isComingSoon && <Badge className="bg-slate-100 text-slate-500 border border-slate-200">Coming Soon</Badge>}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{program.description}</p>

                {program.certifications?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {program.certifications.map((cert, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-xs font-medium bg-brand-gold/10 text-brand-navy px-2.5 py-1 rounded-full">
                        <GraduationCap className="w-3 h-3" /> {cert}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Cohort dates */}
        {!isComingSoon && (
          <>
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full flex items-center justify-between px-6 sm:px-8 py-3 bg-slate-50 border-t border-slate-100 text-brand-navy hover:bg-brand-navy hover:text-white transition-colors"
              data-testid={`toggle-cohorts-${program.program_id}`}
            >
              <span className="text-sm font-semibold">View Upcoming Start Dates</span>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {expanded && (
              <div className="px-6 sm:px-8 py-4 border-t border-slate-100 space-y-3">
                {program.cohorts.map((cohort) => {
                  const spotsLeft = (cohort.max_participants || 0) - (cohort.enrolled_count || 0);
                  return (
                    <div key={cohort.cohort_id} className="flex items-center justify-between p-4 bg-surface-muted rounded-xl" data-testid={`cohort-${cohort.cohort_id}`}>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">
                          {formatDate(cohort.start_date)}{cohort.end_date ? ` – ${formatDate(cohort.end_date)}` : ""}
                        </p>
                        {cohort.schedule && (
                          <p className="text-xs text-slate-500 mt-0.5">{cohort.schedule}</p>
                        )}
                        <p className="text-xs text-slate-500 mt-0.5">
                          {cohort.instructors?.join(", ")}
                          {cohort.notes ? ` · ${cohort.notes}` : ""} &middot; {spotsLeft} spot{spotsLeft !== 1 ? "s" : ""} available
                        </p>
                      </div>
                      {user ? (
                        <Link to="/apply">
                          <Button size="sm" className="btn-primary h-8 px-4 text-xs" data-testid={`enroll-cohort-${cohort.cohort_id}`}>
                            Enroll <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        </Link>
                      ) : (
                        <Button size="sm" className="btn-primary h-8 px-4 text-xs" onClick={login} data-testid={`enroll-cohort-${cohort.cohort_id}`}>
                          Enroll <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Program Materials / Curriculum */}
        <button
          onClick={toggleMaterials}
          className="w-full flex items-center justify-between px-6 sm:px-8 py-3 bg-white border-t border-slate-100 text-brand-navy hover:bg-brand-navy hover:text-white transition-colors group"
          data-testid={`toggle-materials-${program.program_id}`}
        >
          <span className="text-sm font-semibold flex items-center gap-2">
            <FileText className="w-4 h-4 text-brand-gold" />
            View Program Materials
            {materials !== null && totalMaterials > 0 && (
              <span className="bg-brand-navy/10 text-brand-navy group-hover:bg-white/20 group-hover:text-white text-[10px] font-bold rounded-full px-2 py-0.5 transition-colors">
                {totalMaterials}
              </span>
            )}
          </span>
          {materialsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {materialsOpen && (
          <div className="px-6 sm:px-8 py-4 border-t border-slate-100 bg-slate-50/50 space-y-3" data-testid={`materials-${program.program_id}`}>
            {matLoading ? (
              <p className="text-sm text-slate-500 text-center py-3">Loading materials…</p>
            ) : !materials || totalMaterials === 0 ? (
              <p className="text-sm text-slate-500 text-center py-3">
                Materials for this program are still being prepared. Enrolled participants will see them in their dashboard.
              </p>
            ) : (
              <>
                {materials.program?.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Program curriculum</p>
                    <ul className="space-y-2">
                      {materials.program.map((c) => (
                        <MaterialRow key={c.course_id} item={c} user={user} login={login} />
                      ))}
                    </ul>
                  </div>
                )}
                {materials.general?.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mt-3 mb-2">Cross-program materials</p>
                    <ul className="space-y-2">
                      {materials.general.map((c) => (
                        <MaterialRow key={c.course_id} item={c} user={user} login={login} />
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ProgramsPage = () => {
  const { user, login } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await fetch(`${API}/programs`);
        if (res.ok) setPrograms(await res.json());
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchPrograms();
  }, []);

  const activePrograms = programs.filter(p => p.cohorts?.length > 0);
  const comingSoon = programs.filter(p => !p.cohorts || p.cohorts.length === 0);
  const totalCohorts = activePrograms.reduce((a, p) => a + (p.cohorts?.length || 0), 0);

  return (
    <div className="min-h-screen bg-surface-app" data-testid="programs-page">
      <Header />

      {/* Hero */}
      <section className="bg-brand-navy pt-28 pb-16 md:pt-32 md:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A] via-[#152C6E] to-[#0F1D4A]" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-brand-gold text-brand-navy border-brand-gold mb-6 font-semibold">
            {totalCohorts} Upcoming Cohorts in 2026
          </Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6 tracking-tight">
            Academy Programs
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            Career-focused training programs designed for neurodivergent adults.
            Choose your program and start date to begin your journey.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-white/80">
            <span className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-brand-gold" /> {activePrograms.length} Active Programs</span>
            <span className="flex items-center gap-2"><Users className="w-4 h-4 text-brand-gold" /> 8-20 Participants per Cohort</span>
            <span className="flex items-center gap-2"><GraduationCap className="w-4 h-4 text-brand-gold" /> Industry Certifications</span>
          </div>
        </div>
      </section>

      {/* Programs List */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-brand-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {activePrograms.map((program) => (
              <ProgramCard key={program.program_id} program={program} login={login} user={user} />
            ))}
            {comingSoon.map((program) => (
              <ProgramCard key={program.program_id} program={program} login={login} user={user} />
            ))}
          </div>
        )}
      </div>

      {/* CTA */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">Ready to Start?</h2>
          <p className="text-slate-600 mb-8">Enrollment is free. Complete the application and select your program and preferred start date.</p>
          {user ? (
            <Link to="/apply">
              <Button className="btn-primary px-8 py-3 rounded-xl text-base" data-testid="cta-enroll-btn">
                Begin Enrollment <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          ) : (
            <Button className="btn-primary px-8 py-3 rounded-xl text-base" onClick={login} data-testid="cta-enroll-btn">
              Sign In to Enroll <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ProgramsPage;

// Single material/curriculum item row inside the materials accordion.
const MaterialRow = ({ item, user, login }) => {
  const node = (
    <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200 hover:border-brand-navy/30 hover:shadow-sm transition" data-testid={`material-row-${item.course_id}`}>
      <div className="w-9 h-9 bg-brand-navy/5 rounded-lg flex items-center justify-center flex-shrink-0">
        <PlayCircle className="w-4 h-4 text-brand-navy" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">{item.title}</p>
        <p className="text-[11px] text-slate-500 truncate">
          {item.duration_weeks ? `${item.duration_weeks} wk` : ""}
          {item.duration_weeks && item.skill_level ? " · " : ""}
          {item.skill_level ? <span className="capitalize">{item.skill_level}</span> : ""}
          {item.instructor_name ? ` · ${item.instructor_name}` : ""}
        </p>
      </div>
      {!user && <span className="text-[10px] uppercase tracking-wider text-slate-400">Sign in</span>}
    </div>
  );
  if (user) {
    return (
      <li>
        <Link to={`/courses/${item.course_id}`}>{node}</Link>
      </li>
    );
  }
  return (
    <li>
      <button onClick={login} className="w-full text-left">{node}</button>
    </li>
  );
};
